import { useEffect, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { toggleStyle, menuStyle } from "../../data/dropdownStyles";

function MazeOptions({
  changeLevel,
  changeImage,
  changeGroup,
  changeProgression,
  changePhase,
  levelBool,
  imageBool,
  groupBool,
  progressionBool,
  options,
  mode,
}) {
  return (
    <div style={{ display: "flex", gap: "1rem" }}>
      {mode === "type-progressions" && (
        <>
          {/* Types Dropdown */}
          <Dropdown>
            <Dropdown.Toggle style={toggleStyle} variant="success">
              Type
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {options.types.map((t) => (
                <Dropdown.Item
                  key={t.id}
                  onClick={() => {
                    changeLevel(t.id);
                    changePhase("landing"); // reset to start
                    if (t.defaultMaze) changeImage(t.defaultMaze);
                  }}
                  active={levelBool === t.id}
                >
                  {t.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* Progressions Dropdown */}
          <Dropdown>
            <Dropdown.Toggle style={toggleStyle} variant="success">
              Progression
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {options.progressions.map((p) => (
                <Dropdown.Item
                  key={p.id}
                  onClick={() => {
                    changeImage(p.id);
                    changePhase("landing"); // reset to start
                  }}
                  active={imageBool === p.id}
                >
                  {p.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </>
      )}

      {mode === "progression-groups" && (
        <>
          {/* Progressions Dropdown */}
          <Dropdown>
            <Dropdown.Toggle style={toggleStyle} variant="success">
              Progression
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {options.progressions.map((p) => (
                <Dropdown.Item
                  key={p.id}
                  onClick={() => {
                    changeProgression(p.id);
                    changePhase("maze"); // reset to start
                  }}
                  active={progressionBool === p.id}
                >
                  {p.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* Groups Dropdown */}
          {progressionBool && (
            <Dropdown>
              <Dropdown.Toggle style={toggleStyle} variant="success">
                Group
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {options.progressions
                  .find((p) => p.id === progressionBool)
                  ?.groups.map((g) => (
                    <Dropdown.Item
                      key={g.id}
                      onClick={() => {
                        changeGroup(g.id);
                        changePhase("maze"); // reset to start
                        if (g.mazes?.length) changeImage(g.mazes[0].id); // first maze
                      }}
                      active={groupBool === g.id}
                    >
                      {g.label}
                    </Dropdown.Item>
                  ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </>
      )}
    </div>
  );
}

export default function GenericMazeActivity(props) {
  const { config } = props;

  const [time, setTime] = useState(1000);
  const [image, setImage] = useState("1");
  const [dirPath, setDirPath] = useState(config.basePath);

  const [progression, setProgression] = useState(config.defaultProgression || null);
  const [group, setGroup] = useState(config.defaultGroup || null);
  const [level, setLevel] = useState(config.defaultLevel || null);

  const [phase, setPhase] = useState("landing"); // landing | maze | answer

  // --- PDF update on progression / level / image change ---
  useEffect(() => {
    //console.log("Use effect called", props.changePdf, props.open)
    if (props.changePdf) {
      if (props.open) {
        props.changePdf(buildPath("help"));
      } else {
        props.changePdf(`${config.basePath}HELP.pdf`);
      }
    }
  }, [level, progression, group, image, props.open, props.changePdf]);

  function buildPath(kind) {
    if (config.mode === "type-progressions") {
      if (kind === "help") return `${dirPath}/${level}/${image}HELP.pdf`;
      if (kind === "answer") return `${dirPath}/${level}/${image}Answer.png`;
      if (kind === "landing") return `${dirPath}/${level}/Landing.png`;
      return `${dirPath}/${level}/${image}.png`;
    }
    if (config.mode === "progression-groups") {
      if (kind === "help") return `${dirPath}/${progression}/${group}/${image}HELP.pdf`;
      if (kind === "answer") return `${dirPath}/${progression}/${group}/${image}Answer.png`;
      if (kind === "landing") return `${dirPath}/${progression}/${group}/Landing.png`;
      return `${dirPath}/${progression}/${group}/${image}.png`;
    }
    return "";
  }

  function getMazeList() {
    if (config.mode === "type-progressions") {
      return config.options.progressions.map((p) => ({ id: p.id, label: p.label }));
    }
    if (config.mode === "progression-groups") {
      const currentProg = config.options.progressions.find((p) => p.id === progression);
      const currentGroup = currentProg?.groups.find((g) => g.id === group);
      return currentGroup?.mazes || [];
    }
    return [];
  }

  function handleNext() {
    const mazes = getMazeList();
    if (mazes.length === 0) return;

    const idx = mazes.findIndex((m) => m.id === image);
    if(phase=="answer"){
      const nextIdx = (idx + 1) % mazes.length; // loop around
      setImage(mazes[nextIdx].id);
    }
    setPhase("maze"); // always show maze after next/start
  }

  function handleCheck() {
    setPhase(phase === "maze" ? "answer" : "maze");
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="activitySettingsButtonsContainer">
        <MazeOptions
          flashRate={setTime}
          flashBool={time}
          changeLevel={setLevel}
          levelBool={level}
          changeImage={setImage}
          imageBool={image}
          changeGroup={setGroup}
          groupBool={group}
          changeProgression={setProgression}
          progressionBool={progression}
          changePhase={setPhase}
          options={config.options}
          mode={config.mode}
        />
      </div>

      {props.open && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "90%" }}>
          {phase === "landing" && config.showLanding !== false && (
            <div style={{ textAlign: "center", height: "100%", backgroundColor: "white", padding: "5px" }}>
              <img style={{ height: "100%" }} src={buildPath("landing")} />
            </div>
          )}
          {phase === "maze" && (
            <div style={{ textAlign: "center", height: "100%", backgroundColor: "white", padding: "5px" }}>
              <img style={{ height: "100%" }} src={buildPath("maze")} />
            </div>
          )}
          {phase === "answer" && (
            <div style={{ textAlign: "center", height: "100%", backgroundColor: "white", padding: "5px" }}>
              <img style={{ height: "100%" }} src={buildPath("answer")} />
            </div>
          )}
        </div>
      )}

      <div className="activityActionButtonsContainer">
        <button
          className="activityActionButton"
          onClick={handleCheck}
          disabled={phase !== "maze"}
        >
          Check
        </button>
        <button
          className="activityActionButton"
          onClick={handleNext}
          disabled={phase == "maze"}
        >
          Next
        </button>
      </div>
    </div>
  );
}